// configHelpers — pure, stateless helpers and constants extracted verbatim
// from WebshopConfigScreen.js. No React/MUI/component-state dependencies:
// settings<->config converters, row-to-form mappers, backend enum maps and
// default data. Kept as one module so the monolith holds only the component.

export const DEFAULT_AVAILABLE = [
    { id: 'sales', name: 'Sales products', i18nKey: 'cfg_block_sales' },
    // 'text' is offered as an Available block only on the 404 page tab.
    { id: 'text', name: 'Text', i18nKey: 'cfg_block_text', onlyOn: 'page404', configurable: true },
];

// Style settings: which color paths in `styleSettings` map to which legacy
// setting key. Used both on load (to seed the originalStyleSettingsRef) and
// on save (to diff + patch). Module-scope so we declare it once.
export const STYLE_COLOR_PATHS = [
    { setting: 'COLOR_BODY_BACKGROUND',         path: ['global','background'] },
    { setting: 'COLOR_BODY_TEXT',               path: ['global','text']       },
    { setting: 'COLOR_PRIMARY',                 path: ['global','primary']    },
    { setting: 'COLOR_BACKGROUND_LANG',         path: ['sections','language','bgColor']     },
    { setting: 'COLOR_TEXT_LANG',               path: ['sections','language','textColor']   },
    { setting: 'COLOR_TEXT_ACTIVE_LANG',        path: ['sections','language','activeColor'] },
    { setting: 'COLOR_BACKGROUND_HEADER',       path: ['sections','header','bgColor']       },
    { setting: 'COLOR_TEXT_HEADER',             path: ['sections','header','textColor']     },
    { setting: 'COLOR_SECONDARY',               path: ['sections','menu','bgColor']         },
    { setting: 'COLOR_SECONDARY_INVERTED',      path: ['sections','menu','textColor']       },
    { setting: 'COLOR_IN_CART_BACKGROUND',      path: ['sections','menu','accentColor']     },
    // Custom hover colours — gated by the COLOR_USE_CUSTOM_HOVER toggle (a
    // boolean handled separately); the colour values themselves round-trip here.
    { setting: 'COLOR_PRIMARY_HOVER',           path: ['sections','menu','primaryHover']       },
    { setting: 'COLOR_PRIMARY_HOVER_TEXT',      path: ['sections','menu','primaryHoverText']   },
    { setting: 'COLOR_SECONDARY_HOVER',         path: ['sections','menu','secondaryHover']     },
    { setting: 'COLOR_SECONDARY_HOVER_TEXT',    path: ['sections','menu','secondaryHoverText'] },
    { setting: 'COLOR_SALES_VALUE_PRIMARY',     path: ['sections','price','discountColor']  },
    { setting: 'COLOR_BORDERS',                 path: ['sections','sideFilter','borderColor'] },
    { setting: 'COLOR_BACKGROUND_FOOTER',       path: ['sections','footer','bgColor']         },
    { setting: 'COLOR_TEXT_FOOTER',             path: ['sections','footer','textColor']       },
    { setting: 'COLOR_BACKGROUND_FOOTER_EXTRA', path: ['sections','footerExtra','bgColor']    },
    { setting: 'COLOR_TEXT_FOOTER_EXTRA',       path: ['sections','footerExtra','textColor']  },
];
export const readByPath = (obj, path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

// The webshop URL lives on `selectedWebshop.display_name` (lic_clients_webshop)
// and frequently has NO protocol (e.g. "testshop.fashionretail.be") — Header.js
// prepends https:// for exactly this reason. Image/asset URLs and the
// generate_statics iframe are built from this base, so a protocol-less value
// yields a RELATIVE url that 404s against the admin domain (broken logo →
// placeholder SVG, blank carousel/grid tiles). Normalise once, everywhere:
// strip the mini-api suffix + trailing slash, then guarantee a protocol.
export const shopImageBase = (raw) => {
    let u = (raw || '').replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/$/, '');
    if (u && !/^https?:\/\//i.test(u)) u = `https://${u}`;
    return u;
};

// Pure settings-coercion helpers. Hoisted to module scope so they're available
// throughout the load effect regardless of where in the effect body they're
// first referenced (a local `const` declaration mid-effect created a temporal
// dead zone: the font block read `readBool` before its declaration → the whole
// load threw and logo/carousel/grid never initialised).
export const readNum  = (v, fb) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };
export const readBool = (v, fb) => {
    if (v === true || v === 1 || v === '1') return true;
    if (v === false || v === 0 || v === '0') return false;
    return fb;
};
export const readStr  = (v, fb) => (v == null || v === '' ? fb : String(v));

// Canonical front-page block catalog. IDs MUST match what the storefront
// stores in HOME_SECTIONS_ORDER (camelCase where applicable). Product
// containers are added to this list dynamically once fetched, with
// `pd_cnt-<dbId>` ids — matching the legacy admin convention. The `name`
// field is the technical fallback; the visible label comes from `i18nKey`
// so admins see "Welkom" in NL, "Welcome" in EN, etc.
// `onlyOn` scopes a block to one page: 'home' blocks (the landing-page hero /
// product sections) are not offered on the 404 page, which only supports a
// text block + product containers. 'page404' scopes a block to the 404 page.
// Product containers carry no `onlyOn`, so they're available on both.
export const STATIC_BLOCKS = [
    { id: 'carousel',    name: 'Carousel',       i18nKey: 'cfg_block_carousel',     onlyOn: 'home', configurable: true },
    { id: 'imageGrid',   name: 'Image grid',     i18nKey: 'cfg_block_image_grid',   onlyOn: 'home', configurable: true },
    { id: 'intro',       name: 'Welcome',        i18nKey: 'cfg_block_intro',        onlyOn: 'home', configurable: true },
    { id: 'newProducts', name: 'New products',   i18nKey: 'cfg_block_new_products', onlyOn: 'home' },
    { id: 'brands',      name: 'Brands',         i18nKey: 'cfg_block_brands',       onlyOn: 'home' },
    { id: 'sales',       name: 'Sales products', i18nKey: 'cfg_block_sales',        onlyOn: 'home' },
    { id: 'text',        name: 'Text',           i18nKey: 'cfg_block_text',         onlyOn: 'page404', configurable: true },
];

// System / web-safe font stacks — rendered as a "default" group at the top
// of the font dropdown. These don't need a Google Fonts <link> injection
// because the browser resolves them from the OS.
export const DEFAULT_FONTS = [
    { family: '"Noto Serif", serif',                          category: 'default' },
    { family: 'Roboto, "Helvetica Neue", sans-serif',         category: 'default' },
    { family: 'Georgia, serif',                               category: 'default' },
    { family: 'Arial, Helvetica, sans-serif',                 category: 'default' },
    { family: '"Comic Sans MS", "Comic Sans", cursive',       category: 'default' },
    { family: 'Verdana, Geneva, sans-serif',                  category: 'default' },
];

// Compute a readable text color (black/white) against a given hex background.
export const getContrastText = (hex) => {
    try {
        const h = String(hex).replace('#', '').trim();
        if (h.length !== 6) return '#000000';
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.55 ? '#000000' : '#ffffff';
    } catch { return '#000000'; }
};

export const DEFAULT_CONTAINER = {
    reference: '',
    link: '',
    maxArticles: 12,
    maxWidth: 1200,
    topMargin: 0,
    bottomMargin: 0,
    viewMethod: 'horizontal',
    scrollMethod: 'arrows_large',
    active: true,
    keepVisible: true,
    titleNL: '',
    titleEN: '',
    titleFR: '',
    buttonNL: '',
    buttonEN: '',
    buttonFR: '',
    titleFontSize: 1,
    buttonColor: 'primary',
    titlePosition: 'left',
    useIframe: true,
    designNL: null,
    designEN: null,
    designFR: null,
};

// Translate a raw `product_containers` DB row into the form-state shape the
// configure modal uses. Anything we can confidently map gets transferred;
// fields we don't (yet) understand cleanly keep their DEFAULT value, so the
// form still renders in a sensible state. The `body_*` HTML columns are
// intentionally NOT mapped onto designNL/EN/FR — those are unlayer JSON
// designs, a different shape from raw HTML; importing legacy HTML into
// unlayer is a separate task.
export const containerRowToFormData = (row) => {
    if (!row) return DEFAULT_CONTAINER;
    const num = (v, fallback) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
    };
    const bool = (v) => Number(v) === 1 || v === true || v === '1';
    const [marginTop, marginBottom] = (() => {
        const raw = String(row.margin || '').trim();
        if (!raw) return [DEFAULT_CONTAINER.topMargin, DEFAULT_CONTAINER.bottomMargin];
        const [t, b] = raw.split(',').map(s => Number(s.trim()));
        return [
            Number.isFinite(t) ? t : DEFAULT_CONTAINER.topMargin,
            Number.isFinite(b) ? b : DEFAULT_CONTAINER.bottomMargin,
        ];
    })();
    return {
        ...DEFAULT_CONTAINER,
        reference:     row.description    || '',
        titleNL:       row.loc_nl         || '',
        titleEN:       row.loc_en         || '',
        titleFR:       row.loc_fr         || '',
        buttonNL:      row.button_nl      || '',
        buttonEN:      row.button_en      || '',
        buttonFR:      row.button_fr      || '',
        buttonColor:   row.button_type    || DEFAULT_CONTAINER.buttonColor,
        link:          row.explore_url    || '',
        maxArticles:   num(row.max_items,  DEFAULT_CONTAINER.maxArticles),
        maxWidth:      num(row.max_width,  DEFAULT_CONTAINER.maxWidth),
        topMargin:     marginTop,
        bottomMargin:  marginBottom,
        viewMethod:    row.display_style  || DEFAULT_CONTAINER.viewMethod,
        scrollMethod:  row.scroll_style   || DEFAULT_CONTAINER.scrollMethod,
        titleFontSize: num(row.title_size, DEFAULT_CONTAINER.titleFontSize),
        titlePosition: (row.title_position || DEFAULT_CONTAINER.titlePosition).toLowerCase(),
        active:        bool(row.is_active),
        keepVisible:   bool(row.show_when_no_items),
        useIframe:     bool(row.use_iframe),
    };
};

// Inverse of containerRowToFormData: the modal's form shape → the DB columns the
// mini-api PC_FIELDS whitelist accepts (POST/PATCH). Scalar config only — body_*
// content is handled separately (containerBodyContent), never sent on PATCH so an
// existing container's HTML is preserved (the modal doesn't preload it to edit).
export const containerFormToBody = (c = {}) => {
    const bi = (v) => (v ? 1 : 0);
    const n  = (v, fb) => { const x = Number(v); return Number.isFinite(x) ? x : fb; };
    return {
        description:        c.reference || '',
        loc_nl:            c.titleNL || '',  loc_en: c.titleEN || '',  loc_fr: c.titleFR || '',
        button_nl:         c.buttonNL || '', button_en: c.buttonEN || '', button_fr: c.buttonFR || '',
        button_type:       c.buttonColor || 'primary',
        title_size:        n(c.titleFontSize, 1),
        title_position:    c.titlePosition || 'left',
        explore_url:       c.link || '',
        max_items:         n(c.maxArticles, 12),
        max_width:         n(c.maxWidth, 0),
        margin:            `${n(c.topMargin, 0)},${n(c.bottomMargin, 0)}`,
        display_style:     c.viewMethod || 'horizontal',
        scroll_style:      c.scrollMethod || 'arrows_large',
        show_when_no_items: bi(c.keepVisible),
        is_active:         bi(c.active),
        use_iframe:        bi(c.useIframe),
    };
};

// body_{nl,en,fr}: { html, design } captured from the unlayer editors (same
// shape dynamic pages POST). Returned ONLY when the editor actually holds
// authored content — so an update whose editor didn't (re)load a design leaves
// the existing body_* + .html/.json sidecars untouched instead of wiping them.
// `is_via_file:1` rides along so LangFileSync writes the sidecars.
export const containerHasBodyContent = (c = {}) => {
    const rows = (d) => (d && d.body && Array.isArray(d.body.rows) ? d.body.rows.length : 0);
    return rows(c.designNL) > 0 || rows(c.designEN) > 0 || rows(c.designFR) > 0;
};
export const containerBodyContent = (c = {}) => {
    if (!containerHasBodyContent(c)) return {};
    const mk = (html, design) => ({ html: html || '', design: design || null });
    return {
        is_via_file: 1,
        body_nl: mk(c.htmlNL, c.designNL),
        body_en: mk(c.htmlEN, c.designEN),
        body_fr: mk(c.htmlFR, c.designFR),
    };
};

export const DEFAULT_ACTIVE = [
    { id: 'carousel',    name: 'Carousel',     i18nKey: 'cfg_block_carousel',     configurable: true },
    { id: 'intro',       name: 'Welcome',      i18nKey: 'cfg_block_intro',        configurable: true },
    { id: 'imagegrid',   name: 'Image grid',   i18nKey: 'cfg_block_image_grid',   configurable: true },
    { id: 'newproducts', name: 'New products', i18nKey: 'cfg_block_new_products' },
    { id: 'brands',      name: 'Brands',       i18nKey: 'cfg_block_brands' },
];

// 404 page starts empty — admins drag product containers onto it from Available.
export const DEFAULT_ACTIVE_404 = [];

// Login/account cards shown on the checkout page, pre-populated with the
// two blocks that used to live in the legacy config screen.
export const DEFAULT_CARDS = [
    {
        id: 'existing_account',
        group: 'account',
        label: 'Sign in with an existing account',
        useIframe: false,
        contentNL: '<h3>Aanmelden</h3><p>Vul je e-mailadres en wachtwoord in om je bestelling snel af te ronden.</p>',
        contentEN: '<h3>Sign in</h3><p>Enter your email and password to finish your order quickly.</p>',
        contentFR: '<h3>Se connecter</h3><p>Saisissez votre e-mail et votre mot de passe pour finaliser votre commande.</p>',
    },
    {
        id: 'create_account',
        group: 'account',
        label: 'Create a new account',
        useIframe: false,
        contentNL: '<h3>Account aanmaken</h3><p>Maak een account aan om je bestellingen te volgen.</p>',
        contentEN: '<h3>Create account</h3><p>Create an account so you can track your orders.</p>',
        contentFR: '<h3>Créer un compte</h3><p>Créez un compte pour suivre vos commandes.</p>',
    },
    {
        id: 'without_account',
        group: 'account',
        label: 'Continue without an account',
        useIframe: false,
        contentNL: '<h3>Zonder account bestellen</h3><p>Rond je bestelling af zonder een account aan te maken.</p>',
        contentEN: '<h3>Order without an account</h3><p>Complete your order without creating an account.</p>',
        contentFR: '<h3>Commander sans compte</h3><p>Finalisez votre commande sans créer de compte.</p>',
    },
    {
        id: 'pickup',
        group: 'delivery',
        label: 'Order - Pickup',
        useIframe: false,
        contentNL: '<h3>Afhaling</h3><p>Haal je bestelling op in de winkel.</p>',
        contentEN: '<h3>Pickup</h3><p>Collect your order in the store.</p>',
        contentFR: '<h3>Retrait</h3><p>Retirez votre commande en magasin.</p>',
    },
    {
        id: 'delivery',
        group: 'delivery',
        label: 'Order - Delivery',
        useIframe: false,
        contentNL: '<h3>Verzending</h3><p>Laat je bestelling thuis leveren.</p>',
        contentEN: '<h3>Delivery</h3><p>Have your order delivered to your home.</p>',
        contentFR: '<h3>Livraison</h3><p>Faites livrer votre commande à domicile.</p>',
    },
    {
        // Short note shown at the end of the order-confirmation screen
        // (legacy "Orderbevestiging notitie" / ORDER_CONFIRMATION_FOOTER).
        id: 'confirmation_footer',
        group: 'confirmation',
        label: 'Order confirmation note',
        useIframe: false,
        contentNL: '', contentEN: '', contentFR: '',
    },
];

// Cart-page cards ↔ `contents` rows. The cart flow has account blocks (login,
// register, without) and delivery-method blocks (pickup, delivery); each is a
// wysiwyg content row. Loaded from the settings dump (s.contents_*), saved via
// PATCH /admin/contents/{attr}.
export const CARD_CONTENT = {
    existing_account: { attr: 'TEXT_ORDER_ACCOUNT_LOGIN',    field: 'contents_order_login' },
    create_account:   { attr: 'TEXT_ORDER_ACCOUNT_REGISTER', field: 'contents_order_register' },
    without_account:  { attr: 'TEXT_ORDER_ACCOUNT_WITHOUT',  field: 'contents_order_without' },
    pickup:           { attr: 'TEXT_ORDER_PICKUP',           field: 'contents_text_order_pickup' },
    delivery:         { attr: 'TEXT_ORDER_DELIVERY',         field: 'contents_text_order_delivery' },
    confirmation_footer: { attr: 'ORDER_CONFIRMATION_FOOTER', field: 'contents_order_confirmation_footer' },
};

// Brands page layout ↔ the storefront's BRANDS_PAGE_STYLE value.
export const BRANDS_LAYOUT_TO_STYLE = { continuous: 'list', grouped: 'alphabetical-list', multiCol: 'alphabetical-columns' };
export const BRANDS_STYLE_TO_LAYOUT = { 'list': 'continuous', 'alphabetical-list': 'grouped', 'alphabetical-columns': 'multiCol' };

// Dynamic-page position ↔ the backend `dynamic_pages.position` column.
export const DYN_POS_TO_BACKEND   = { hidden: 'HIDDEN', header: 'HEADER', footer: 'FOOTER', both: 'BOTH' };
export const DYN_POS_FROM_BACKEND = { HIDDEN: 'hidden', HEADER: 'header', FOOTER: 'footer', BOTH: 'both' };
export const DYN_LANGS = ['nl', 'en', 'fr'];

// Information banner position ↔ backend `information_banners.position`. UI
// 'top' = above the navbar (backend 'top'); 'below' = below the menu/navbar
// (backend 'navbar', per mig147).
export const BANNER_POS_TO_BACKEND   = { top: 'top', below: 'navbar' };
export const BANNER_POS_FROM_BACKEND = { top: 'top', navbar: 'below' };

// Cookie banner style ↔ vanilla-cookieconsent layout (cookie.js guiOptions).
export const OVERIGE_STYLE_TO_LAYOUT      = { floating: 'cloud', modal: 'box', bottom: 'bar' };
export const LAYOUT_TO_OVERIGE_STYLE      = { cloud: 'floating', box: 'modal', bar: 'bottom' };

// Cadeaubon (gift voucher) — the CHEQUES_* settings are stored as strings
// ('1'/'0' for booleans, CSV for amounts, enums for types). These helpers map
// the flat settings dump to the CadeaubonTab config shape and back.
export const chq01   = (v) => v === true || v === 1 || v === '1';
export const chqNum  = (v, dflt) => { const n = Number(v); return Number.isFinite(n) ? n : dflt; };
export const chqAmts = (v) => String(v == null ? '' : v).split(',').map(x => parseInt(x.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
// Voucher-type enum for the personalization selects (matches the old admin).
export const CHEQUE_TYPE = ['NONE', 'PHYSICAL', 'DIGITAL', 'BOTH'];
export const chqType = (v, dflt) => { const u = String(v == null ? '' : v).toUpperCase(); return CHEQUE_TYPE.includes(u) ? u : dflt; };

export const settingsToCadeaubon = (s, dflt) => {
    const types = String(s.CHEQUES_AVAILABLE_TYPES || 'BOTH').toUpperCase();
    const presets = chqAmts(s.CHEQUES_AVAILABLE_AMOUNTS);
    return {
        availability: {
            showInNavigation: chq01(s.SHOW_HEADER_CHEQUES),
            allowPhysical:    types === 'BOTH' || types === 'PHYSICAL',
            allowDigital:     types === 'BOTH' || types === 'DIGITAL',
        },
        amounts: {
            presets:     presets.length ? presets : dflt.amounts.presets,
            allowCustom: chq01(s.CHEQUES_ALLOW_CUSTOM_AMOUNT),
            customMin:   chqNum(s.CHEQUES_MIN_AMOUNT, dflt.amounts.customMin),
            customMax:   chqNum(s.CHEQUES_MAX_AMOUNT, dflt.amounts.customMax),
        },
        discounts: {
            countTowardTotals:  chq01(s.CHEQUES_ADD_TO_CUSTOMER_DISCOUNT),
            applyToVoucherOnly: chq01(s.CHEQUES_ALLOW_CUSTOMER_DISC),
            applyToMixedOrders: chq01(s.CHEQUES_ALLOW_CUSTOMER_DISC_MIX),
        },
        shipping: {
            cost:     chqNum(s.CHEQUES_SHIPMENT_COSTS, dflt.shipping.cost),
            freeFrom: chqNum(s.CHEQUES_SHIPMENT_FREE_AMOUNT, dflt.shipping.freeFrom),
        },
        personalization: {
            infoOnType:          chqType(s.CHEQUES_ALLOW_INFO_ON_TYPE, dflt.personalization.infoOnType),
            sendDigitalDirectly: chq01(s.CHEQUES_ALLOW_SEND_TO_RECEIVER),
            messageOnType:       chqType(s.CHEQUES_ALLOW_MESSAGE_ON_TYPE, dflt.personalization.messageOnType),
        },
    };
};

// CadeaubonTab config → flat { SETTING_KEY: storedStringValue } map. Booleans as
// '1'/'0', amounts as a ', '-joined CSV, enums for types/message — matching what
// the backend stores so the dirty-diff compares like-for-like.
export const cadeaubonToSettings = (c) => {
    const a = c.availability || {}, am = c.amounts || {}, d = c.discounts || {}, sh = c.shipping || {}, p = c.personalization || {};
    const types = (a.allowPhysical && a.allowDigital) ? 'BOTH' : a.allowDigital ? 'DIGITAL' : a.allowPhysical ? 'PHYSICAL' : 'BOTH';
    return {
        SHOW_HEADER_CHEQUES:              a.showInNavigation ? '1' : '0',
        CHEQUES_AVAILABLE_TYPES:          types,
        CHEQUES_AVAILABLE_AMOUNTS:        (am.presets || []).join(', '),
        CHEQUES_ALLOW_CUSTOM_AMOUNT:      am.allowCustom ? '1' : '0',
        CHEQUES_MIN_AMOUNT:               String(am.customMin ?? ''),
        CHEQUES_MAX_AMOUNT:               String(am.customMax ?? ''),
        CHEQUES_SHIPMENT_COSTS:           String(sh.cost ?? ''),
        CHEQUES_SHIPMENT_FREE_AMOUNT:     String(sh.freeFrom ?? ''),
        CHEQUES_ALLOW_INFO_ON_TYPE:       chqType(p.infoOnType, 'NONE'),
        CHEQUES_ALLOW_MESSAGE_ON_TYPE:    chqType(p.messageOnType, 'NONE'),
        CHEQUES_ALLOW_SEND_TO_RECEIVER:   p.sendDigitalDirectly ? '1' : '0',
        CHEQUES_ADD_TO_CUSTOMER_DISCOUNT: d.countTowardTotals ? '1' : '0',
        CHEQUES_ALLOW_CUSTOMER_DISC:      d.applyToVoucherOnly ? '1' : '0',
        CHEQUES_ALLOW_CUSTOMER_DISC_MIX:  d.applyToMixedOrders ? '1' : '0',
    };
};

// Backend `cheques_placeholders` row → the Translations-table row shape. The UI
// edits loc_*/description; `key` is server-generated and shown read-only.
export const placeholderRowToForm = (row) => ({
    id:          row.id,
    key:         row.key || '',
    description: row.description || '',
    layout:      row.layout || 'GLOBAL',
    nl:          row.loc_nl || '',
    en:          row.loc_en || '',
    fr:          row.loc_fr || '',
});

// Cheque/voucher layout (cheques_layouts) row → UI shape. `imgBase` is the
// resolved `<webshopBase><img_path>/cheque-layouts/` URL prefix for previews.
// `cheque_type` ≠ null marks a built-in system default (not deletable). For
// PHYSICAL layouts `layout_file` is the uploaded preview image; for DIGITAL it's
// the `<id>.html` design file (not an image), so we only surface it as an image
// for physical layouts.
export const layoutRowToForm = (row, imgBase) => {
    const type = String(row.type || 'DIGITAL').toUpperCase();
    const isImageFile = (fn) => fn && !/\.html?$/i.test(String(fn));
    return {
        id:          `lay_${row.id}`,
        serverId:    row.id,
        type,
        description: row.description || '',
        visible:     !!Number(row.visible),
        isSystem:    !!(row.cheque_type != null && String(row.cheque_type).trim() !== ''),
        chequeType:  row.cheque_type || null,
        image:       row.image ? { name: row.image, url: `${imgBase}${row.image}` } : null,
        layoutImage: (type === 'PHYSICAL' && isImageFile(row.layout_file))
            ? { name: row.layout_file, url: `${imgBase}${row.layout_file}` } : null,
        // pending pickers + design dirty (set in the tab; cleared on save/refetch)
        imageFile: null, layoutImageFile: null,
        designDirty: false, design: null, html: null, designLoaded: false,
        copyFrom: null,
    };
};

// Stable signature for the layouts dirty-diff (covers metadata, pending image
// picks, and a dirty Unlayer design).
export const layoutCompareJson = (arr) => JSON.stringify((arr || []).map(l => ({
    id:          l.serverId ?? null,
    type:        l.type,
    description: l.description || '',
    visible:     !!l.visible,
    copyFrom:    l.copyFrom ?? null,
    img:         l.imageFile ? `PENDING:${l.imageFile.name}` : (l.image?.name || ''),
    layoutImg:   l.layoutImageFile ? `PENDING:${l.layoutImageFile.name}` : (l.layoutImage?.name || ''),
    design:      l.designDirty ? 'PENDING' : '',
})));

// Custom customer fields = `customers_categories_groups` rows (CRUD). The panel
// uses {id, codeGroup(=sft_id), labels{nl,en,fr}, showOn{registration,account,newsletter}}.
export const customerGroupRowToForm = (row) => ({
    id:        row.id,
    codeGroup: String(row.sft_id || ''),
    labels:    { nl: row.loc_nl || '', en: row.loc_en || '', fr: row.loc_fr || '' },
    showOn: {
        registration: chq01(row.show_on_signup),
        account:      chq01(row.show_on_account),
        newsletter:   chq01(row.show_on_newsletter),
    },
});
export const customerGroupToColumns = (g) => ({
    sft_id:             g.codeGroup || '',
    loc_nl:             g.labels?.nl || '',
    loc_en:             g.labels?.en || '',
    loc_fr:             g.labels?.fr || '',
    show_on_signup:     g.showOn?.registration ? 1 : 0,
    show_on_account:    g.showOn?.account ? 1 : 0,
    show_on_newsletter: g.showOn?.newsletter ? 1 : 0,
});

// ── Verwerking (order processing) — settings mapping ──────────────────────
// Only fields with a confirmed settings-key backend are wired here. Fields
// without one (general discount type, negative-cart, pickup cost, country
// preset/rates, per-store pickup, payment provider/type split, the "no
// checkout" payment option) stay in the UI as pending and are intentionally
// excluded from this map so they neither save nor flag dirty.
export const PROC_PAY_LOC_TO_BACKEND   = { inStore: 'IN_STORE', onlineOnly: 'ONLINE', onlineAndStore: 'BOTH', noCheckout: 'NOCHECKOUT' };
export const PROC_PAY_LOC_FROM_BACKEND = { IN_STORE: 'inStore', ONLINE: 'onlineOnly', BOTH: 'onlineAndStore' };
export const PROC_SHIP_TO_BACKEND      = { pickupOnly: 'PICKUP', shippingOnly: 'SHIPMENT', pickupAndShipping: 'BOTH' };
export const PROC_SHIP_FROM_BACKEND    = { PICKUP: 'pickupOnly', SHIPMENT: 'shippingOnly', BOTH: 'pickupAndShipping' };
// PICKUP_NO_STOCK_ICON stores a FontAwesome class string ("fas fa-truck-ramp-box");
// the IconPicker works with the dash-case name ("truck-ramp-box").
export const iconClassToName = (cls) => { const m = String(cls || '').match(/fa-([a-z0-9-]+)/i); return m ? m[1] : ''; };
export const iconNameToClass = (name) => (name ? `fas fa-${name}` : '');
// Discount/voucher type codes: the UI rows use 6-digit codes, the CHEQUES_*
// JSON settings key them as 8-digit ("99" + the 6-digit code).
export const DISC_CODE8 = (code6) => `99${code6}`;

// POS-betaalmiddelen (kas_betaal1..9) zijn per winkel/kassa gesegmenteerd via
// kasvar.code: '0000' = globaal, '00'+winkel = winkel-specifiek, de volledige
// POS-id (bv. '0101') = kassa-specifiek. Per slot kiezen we de meest specifieke
// rij voor de gekozen POS-id (PROCESS_RECEIPT_POS_ID_PROCESS), met fallback
// kassa → winkel → globaal — dezelfde volgorde als de backend
// (ProgVariable::scopeForLocation: code IN ('0000','00'+store, pos)). Rijen van
// andere winkels/kassa's worden genegeerd. `rows` = [{n, value, code}].
export const computePaymentTypeOptions = (rows, posIdRaw) => {
    const posId = String(posIdRaw || '').trim();
    const storeCode = posId.length >= 2 ? '00' + posId.slice(0, 2) : '';
    const rank = (code) => {
        const c = String(code == null ? '' : code).trim();
        if (posId && c === posId) return 3;          // kassa-specifiek
        if (storeCode && c === storeCode) return 2;  // winkel-specifiek
        if (c === '0000') return 1;                  // globaal
        return 0;                                    // andere winkel/kassa
    };
    const best = {};
    for (const r of (rows || [])) {
        const score = rank(r.code);
        if (score === 0) continue;
        if (!best[r.n] || score > best[r.n].score) best[r.n] = { value: r.value, score };
    }
    return Object.keys(best)
        .map(Number)
        .sort((a, b) => a - b)
        .map(n => ({ value: String(n), label: `${n} — ${best[n].value}` }));
};

// Read a File as a data URL (base64). Uploads travel as base64 in the JSON
// proxy body; the mini-api's UploadStore::saveBase64 strips the data-URL prefix.
export const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
});

export const settingsToProcessing = (s, dflt) => ({
    access: {
        ...dflt.access,
        posCode:          s.CUSTOMER_CREATION_POS_ID != null && s.CUSTOMER_CREATION_POS_ID !== '' ? String(s.CUSTOMER_CREATION_POS_ID) : dflt.access.posCode,
        guestCustomerId:  s.PROCESS_NO_ACCOUNT_ID != null ? String(s.PROCESS_NO_ACCOUNT_ID) : dflt.access.guestCustomerId,
        allowGuestPickup: chq01(s.PROCESS_ALLOW_WITHOUT_ACCOUNT),
    },
    warehouse: s.PROCESS_STORE != null && s.PROCESS_STORE !== '' ? String(s.PROCESS_STORE) : dflt.warehouse,
    receiptPos: {
        process: s.PROCESS_RECEIPT_POS_ID_PROCESS != null ? String(s.PROCESS_RECEIPT_POS_ID_PROCESS) : dflt.receiptPos.process,
        print:   s.PROCESS_RECEIPT_POS_ID_RECEIPT != null ? String(s.PROCESS_RECEIPT_POS_ID_RECEIPT) : dflt.receiptPos.print,
    },
    discountTypes: (() => {
        const allowed = (s.CHEQUES_ALLOWED && typeof s.CHEQUES_ALLOWED === 'object') ? s.CHEQUES_ALLOWED : {};
        const asPay   = (s.CHEQUES_AS_PAYMENT && typeof s.CHEQUES_AS_PAYMENT === 'object') ? s.CHEQUES_AS_PAYMENT : {};
        const receipt = (s.CHEQUES_ADD_TO_RECEIPT && typeof s.CHEQUES_ADD_TO_RECEIPT === 'object') ? s.CHEQUES_ADD_TO_RECEIPT : {};
        const out = {};
        Object.keys(dflt.discountTypes).forEach((code6) => {
            const k8 = DISC_CODE8(code6);
            out[code6] = { enabled: !!allowed[k8], acceptAsPayment: !!asPay[k8], showInEmail: !!receipt[k8] };
        });
        return out;
    })(),
    discountBarcode: s.ITEM_DISCOUNT != null ? String(s.ITEM_DISCOUNT) : '',
    cart: {
        ...dflt.cart,
        allowDiscountCodes:   chq01(s.ENABLE_VOUCHERS),
        allowNegativeTotals:  chq01(s.CHEQUE_USAGE_VALUE_BIGGER),
        limitQuantityPerItem: chq01(s.CART_MAX_QTY_LIMIT),
        maxQuantityPerItem:   chqNum(s.CART_MAX_QTY,   dflt.cart.maxQuantityPerItem),
        maxUniqueItemsInCart: chqNum(s.CART_MAX_ITEMS, dflt.cart.maxUniqueItemsInCart),
    },
    registration: {
        ...dflt.registration,
        phoneRequired:        chq01(s.CUSTOMER_CREATION_PHONE_REQ),
        defaultSalutation:    s.CUSTOMER_DEFAULT_TITLE != null && s.CUSTOMER_DEFAULT_TITLE !== '' ? String(s.CUSTOMER_DEFAULT_TITLE) : dflt.registration.defaultSalutation,
        defaultCountry:       s.CUSTOMER_DEFAULT_COUNTRY != null ? String(s.CUSTOMER_DEFAULT_COUNTRY) : dflt.registration.defaultCountry,
        showPurchaseHistory:  chq01(s.ACCOUNT_SHOW_ORDERS),
        showCustomerDiscount: chq01(s.ACCOUNT_SHOW_DISCOUNT),
    },
    externalStock: {
        iconName:  iconClassToName(s.PICKUP_NO_STOCK_ICON) || dflt.externalStock.iconName,
        iconColor: s.PICKUP_NO_STOCK_COLOR || dflt.externalStock.iconColor,
    },
    ticketPrinting: {
        regular: { pickup: chq01(s.PROCESS_PRINT_TICKET_PICK),     shipped: chq01(s.PROCESS_PRINT_TICKET_SHIP) },
        compact: { pickup: chq01(s.PROCESS_PRINT_TICKET_MIN_PICK), shipped: chq01(s.PROCESS_PRINT_TICKET_MIN_SHIP) },
        message: { pickup: chq01(s.PROCESS_PRINT_MESSAGE_PICK),    shipped: chq01(s.PROCESS_PRINT_MESSAGE_SHIP) },
    },
    payments: {
        ...dflt.payments,
        // LOOKBOOK (catalogue) mode = "no checkout"; otherwise map the online/in-store choice.
        location:     String(s.PROCESS_TICKET_MODULE || '').toUpperCase() === 'LOOKBOOK'
            ? 'noCheckout'
            : (PROC_PAY_LOC_FROM_BACKEND[String(s.PROCESS_PAYMENT_METHODS || '').toUpperCase()] || dflt.payments.location),
        allowInvoice: chq01(s.CHECKOUT_ALLOW_INVOICE),
        provider:     s.PAYMENT_PROVIDER ? String(s.PAYMENT_PROVIDER) : dflt.payments.provider,
        paymentType:  s.PROCESS_PAYMENT_TYPE != null && s.PROCESS_PAYMENT_TYPE !== '' ? String(s.PROCESS_PAYMENT_TYPE) : dflt.payments.paymentType,
    },
    shipping: {
        ...dflt.shipping,
        method:                   PROC_SHIP_FROM_BACKEND[String(s.PROCESS_TICKET_DELIVERY_TYPE || '').toUpperCase()] || dflt.shipping.method,
        routeExternalThroughShop: chq01(s.SEND_EXTERNAL_ORDER_TO_STORE),
        externalStockEnabled:     chq01(s.PRODUCT_USE_EXTERNAL_STOCK), // read-only gate (set in Data sync)
        defaultCost:              chqNum(s.SHIPMENT_COST,        dflt.shipping.defaultCost),
        defaultPickupCost:        chqNum(s.PICKUP_POINT_COST,    dflt.shipping.defaultPickupCost),
        freeFromAmount:           chqNum(s.SHIPMENT_FREE_AMOUNT, dflt.shipping.freeFromAmount),
        shippingCostItem:         s.ITEM_SHIPMENT != null ? String(s.ITEM_SHIPMENT) : dflt.shipping.shippingCostItem,
    },
    customerFields: dflt.customerFields, // pending (separate customers_categories_groups table)
});

// Processing config → flat { SETTING_KEY: storedString } for the dirty-diff and
// scalar PATCHes. `__`-prefixed keys are dirty-only signals (e.g. the discount
// JSON cells) and are skipped when building the actual PATCH list. The discount
// JSON objects themselves are rebuilt+patched separately (to preserve any codes
// not in the UI row list).
export const processingToSettings = (c) => {
    const ac = c.access || {}, ca = c.cart || {}, rg = c.registration || {}, es = c.externalStock || {}, tp = c.ticketPrinting || {}, pm = c.payments || {}, sh = c.shipping || {}, dt = c.discountTypes || {};
    const out = {
        CUSTOMER_CREATION_POS_ID:      String(ac.posCode ?? ''),
        PROCESS_NO_ACCOUNT_ID:         String(ac.guestCustomerId ?? ''),
        PROCESS_ALLOW_WITHOUT_ACCOUNT: ac.allowGuestPickup ? '1' : '0',
        PROCESS_STORE:                 String(c.warehouse ?? ''),
        PROCESS_RECEIPT_POS_ID_PROCESS: String(c.receiptPos?.process ?? ''),
        PROCESS_RECEIPT_POS_ID_RECEIPT: String(c.receiptPos?.print   ?? ''),
        ITEM_DISCOUNT:                 String(c.discountBarcode ?? ''),
        ENABLE_VOUCHERS:               ca.allowDiscountCodes ? '1' : '0',
        CHEQUE_USAGE_VALUE_BIGGER:     ca.allowNegativeTotals ? '1' : '0',
        CART_MAX_QTY_LIMIT:            ca.limitQuantityPerItem ? '1' : '0',
        CART_MAX_QTY:                  String(ca.maxQuantityPerItem ?? ''),
        CART_MAX_ITEMS:                String(ca.maxUniqueItemsInCart ?? ''),
        CUSTOMER_CREATION_PHONE_REQ:   rg.phoneRequired ? '1' : '0',
        CUSTOMER_DEFAULT_TITLE:        String(rg.defaultSalutation ?? ''),
        CUSTOMER_DEFAULT_COUNTRY:      String(rg.defaultCountry ?? ''),
        ACCOUNT_SHOW_ORDERS:           rg.showPurchaseHistory ? '1' : '0',
        ACCOUNT_SHOW_DISCOUNT:         rg.showCustomerDiscount ? '1' : '0',
        PICKUP_NO_STOCK_ICON:          iconNameToClass(es.iconName),
        PICKUP_NO_STOCK_COLOR:         String(es.iconColor ?? ''),
        PROCESS_PRINT_TICKET_PICK:     tp.regular?.pickup  ? '1' : '0',
        PROCESS_PRINT_TICKET_SHIP:     tp.regular?.shipped ? '1' : '0',
        PROCESS_PRINT_TICKET_MIN_PICK: tp.compact?.pickup  ? '1' : '0',
        PROCESS_PRINT_TICKET_MIN_SHIP: tp.compact?.shipped ? '1' : '0',
        PROCESS_PRINT_MESSAGE_PICK:    tp.message?.pickup  ? '1' : '0',
        PROCESS_PRINT_MESSAGE_SHIP:    tp.message?.shipped ? '1' : '0',
        PROCESS_PAYMENT_METHODS:       PROC_PAY_LOC_TO_BACKEND[pm.location] || 'BOTH',
        CHECKOUT_ALLOW_INVOICE:        pm.allowInvoice ? '1' : '0',
        PAYMENT_PROVIDER:              pm.provider || 'msp',
        PROCESS_PAYMENT_TYPE:          String(pm.paymentType ?? '1'),
        PROCESS_TICKET_DELIVERY_TYPE:  PROC_SHIP_TO_BACKEND[sh.method] || 'BOTH',
        SEND_EXTERNAL_ORDER_TO_STORE:  sh.routeExternalThroughShop ? '1' : '0',
        SHIPMENT_COST:                 String(sh.defaultCost ?? ''),
        PICKUP_POINT_COST:             String(sh.defaultPickupCost ?? ''),
        SHIPMENT_FREE_AMOUNT:          String(sh.freeFromAmount ?? ''),
        ITEM_SHIPMENT:                 String(sh.shippingCostItem ?? ''),
    };
    // Discount/voucher cells — dirty-only signals (real save rebuilds the JSON).
    Object.keys(dt).forEach((code) => {
        const r = dt[code] || {};
        out[`__DISC_ALLOWED_${code}`] = r.enabled         ? '1' : '0';
        out[`__DISC_ASPAY_${code}`]   = r.acceptAsPayment ? '1' : '0';
        out[`__DISC_RECEIPT_${code}`] = r.showInEmail     ? '1' : '0';
    });
    return out;
};

// ── Synchronisatie (product sync) — settings mapping ──────────────────────
// All controls map onto existing storefront `settings` keys (FETCH_*, MAP_*,
// PHOTO_*), seeded in the storefront migrations. No CRUD, no deploy.
//
// PHOTO_PADDING_COLOR is stored as an "r,g,b" string; the colour input is hex.
export const hexToRgbStr = (hex) => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '').trim());
    if (!m) return '255,255,255';
    return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
};
export const rgbStrToHex = (rgb) => {
    const parts = String(rgb || '').split(',').map(x => parseInt(x.trim(), 10));
    if (parts.length < 3 || parts.slice(0, 3).some(n => Number.isNaN(n))) return '#ffffff';
    const h = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${h(parts[0])}${h(parts[1])}${h(parts[2])}`;
};

// `allStoreCodes` (the full store universe) lets FETCH_FOR_STORES round-trip:
// '00'/'' = all → expanded to every code; an all-selected set → back to '00'.
export const settingsToSync = (s, dflt, allStoreCodes = []) => ({
    sourceWebshop: s.FETCH_FOR_ONLINE != null && s.FETCH_FOR_ONLINE !== '' ? String(s.FETCH_FOR_ONLINE) : dflt.sourceWebshop,
    storesIncluded: (() => {
        const raw = s.FETCH_FOR_STORES != null ? String(s.FETCH_FOR_STORES) : '00';
        if (raw === '' || raw === '00') return [...allStoreCodes];
        return raw.split(',').map(x => x.trim()).filter(Boolean);
    })(),
    stockFiltering: {
        onlySyncWithStock:        chq01(s.FETCH_ONLY_WITH_STOCK),
        useExternalStockProvider: chq01(s.PRODUCT_USE_EXTERNAL_STOCK),
        showExternalOnlyProducts: chq01(s.OVERRULE_ONLY_IN_STOCK_FOR_EXT),
    },
    categoryHierarchy: [1, 2, 3, 4, 5, 6, 7].map((n) => {
        const v = s[`MAP_CATEGORY_${n}`];
        return { level: n, source: v != null && v !== '' ? String(v) : String(n).padStart(2, '0') };
    }),
    productFields: {
        name:      s.MAP_PRODUCT_NAME      != null && s.MAP_PRODUCT_NAME      !== '' ? String(s.MAP_PRODUCT_NAME)      : dflt.productFields.name,
        reference: s.MAP_PRODUCT_REFERENCE != null && s.MAP_PRODUCT_REFERENCE !== '' ? String(s.MAP_PRODUCT_REFERENCE) : dflt.productFields.reference,
    },
    languageMapping: {
        nl: s.MAP_LOCALE_NL != null && s.MAP_LOCALE_NL !== '' ? String(s.MAP_LOCALE_NL) : dflt.languageMapping.nl,
        en: s.MAP_LOCALE_EN != null && s.MAP_LOCALE_EN !== '' ? String(s.MAP_LOCALE_EN) : dflt.languageMapping.en,
        fr: s.MAP_LOCALE_FR != null && s.MAP_LOCALE_FR !== '' ? String(s.MAP_LOCALE_FR) : dflt.languageMapping.fr,
    },
    imageSourceTypes: {
        primary:     s.PHOTO_MAIN_TYPE   != null && s.PHOTO_MAIN_TYPE   !== '' ? String(s.PHOTO_MAIN_TYPE)   : dflt.imageSourceTypes.primary,
        alternative: s.PHOTO_SECOND_TYPE != null && s.PHOTO_SECOND_TYPE !== '' ? String(s.PHOTO_SECOND_TYPE) : dflt.imageSourceTypes.alternative,
    },
    imageProcessing: {
        useV2: chq01(s.USE_IMAGES_CRON_V2),
        sizes: {
            thumb:     { width: chqNum(s.PHOTO_SQUARE_SIZE_THUMB,   100), height: chqNum(s.PHOTO_RATIO_HEIGHT_THUMB,   160)  },
            preview:   { width: chqNum(s.PHOTO_SQUARE_SIZE_PREVIEW, 300), height: chqNum(s.PHOTO_RATIO_HEIGHT_PREVIEW, 500)  },
            processed: { width: chqNum(s.PHOTO_SQUARE_SIZE_FULL,    800), height: chqNum(s.PHOTO_RATIO_HEIGHT_FULL,    1300) },
        },
        backgroundFillColor: rgbStrToHex(s.PHOTO_PADDING_COLOR),
    },
});

export const syncToSettings = (c, allStoreCodes = []) => {
    const sf = c.stockFiltering || {}, ip = c.imageProcessing || {}, sz = ip.sizes || {};
    const sel = Array.isArray(c.storesIncluded) ? c.storesIncluded : [];
    const allSelected = allStoreCodes.length > 0 && allStoreCodes.every(code => sel.includes(code));
    const out = {
        FETCH_FOR_ONLINE:               String(c.sourceWebshop ?? '1'),
        FETCH_FOR_STORES:               allSelected ? '00' : sel.join(','),
        FETCH_ONLY_WITH_STOCK:          sf.onlySyncWithStock        ? '1' : '0',
        PRODUCT_USE_EXTERNAL_STOCK:     sf.useExternalStockProvider ? '1' : '0',
        OVERRULE_ONLY_IN_STOCK_FOR_EXT: sf.showExternalOnlyProducts ? '1' : '0',
        MAP_PRODUCT_NAME:               String(c.productFields?.name ?? ''),
        MAP_PRODUCT_REFERENCE:          String(c.productFields?.reference ?? ''),
        MAP_LOCALE_NL:                  String(c.languageMapping?.nl ?? ''),
        MAP_LOCALE_EN:                  String(c.languageMapping?.en ?? ''),
        MAP_LOCALE_FR:                  String(c.languageMapping?.fr ?? ''),
        PHOTO_MAIN_TYPE:                String(c.imageSourceTypes?.primary ?? ''),
        PHOTO_SECOND_TYPE:              String(c.imageSourceTypes?.alternative ?? ''),
        USE_IMAGES_CRON_V2:             ip.useV2 ? '1' : '0',
        PHOTO_SQUARE_SIZE_THUMB:        String(sz.thumb?.width      ?? ''),
        PHOTO_SQUARE_SIZE_PREVIEW:      String(sz.preview?.width    ?? ''),
        PHOTO_SQUARE_SIZE_FULL:         String(sz.processed?.width  ?? ''),
        PHOTO_RATIO_HEIGHT_THUMB:       String(sz.thumb?.height     ?? ''),
        PHOTO_RATIO_HEIGHT_PREVIEW:     String(sz.preview?.height   ?? ''),
        PHOTO_RATIO_HEIGHT_FULL:        String(sz.processed?.height ?? ''),
        PHOTO_PADDING_COLOR:            hexToRgbStr(ip.backgroundFillColor || '#ffffff'),
    };
    (c.categoryHierarchy || []).forEach((lvl) => {
        out[`MAP_CATEGORY_${lvl.level}`] = String(lvl.source ?? '');
    });
    return out;
};

export const bannerRowToForm = (row) => ({
    id:            `srv_${row.id}`,
    serverId:      row.id,
    referenceName: row.name || '',
    isActive:      !!Number(row.active),
    activeFrom:    row.date_from  ? String(row.date_from).slice(0, 10)  : '',
    activeTo:      row.date_until ? String(row.date_until).slice(0, 10) : '',
    position:      BANNER_POS_FROM_BACKEND[String(row.position || '').toLowerCase()] || 'top',
    bgColor:       row.background_color || '#000000',
    textColor:     row.foreground_color || '#ffffff',
    height:        row.height != null ? Number(row.height) : 30,
    scrollText:    !!Number(row.is_animated),
    multipleItems: !!Number(row.is_item_list),
    editor:        'wysiwyg',
    // Single-banner content lives in loc_*; the item-list variant uses items_*.
    // We surface whichever side matches the banner's current `is_item_list`.
    content: Number(row.is_item_list)
        ? { nl: row.items_nl || '', en: row.items_en || '', fr: row.items_fr || '' }
        : { nl: row.loc_nl   || '', en: row.loc_en   || '', fr: row.loc_fr   || '' },
});

// Backend `dynamic_pages` row → the editor's page shape. `content` starts empty
// (designs are lazy-loaded into the editor) so merely viewing a page can't flag
// the screen dirty; only edited languages land in `content`.
export const dynRowToPage = (row) => ({
    id:       `srv_${row.id}`,
    serverId: row.id,
    slug:     row.route || '',
    position: DYN_POS_FROM_BACKEND[String(row.position || '').toUpperCase()] || 'hidden',
    isActive: !!row.active,
    iframe:   !!row.use_iframe,
    titles:   { nl: row.loc_nl || '', en: row.loc_en || '', fr: row.loc_fr || '' },
    metaTitle:       { nl: row.meta_title_nl || '', en: row.meta_title_en || '', fr: row.meta_title_fr || '' },
    metaDescription: { nl: row.meta_description_nl || '', en: row.meta_description_en || '', fr: row.meta_description_fr || '' },
    content:  {},
});

